import * as React from 'react';
import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';

/** Material UI Packages. */
import { Stack, Box, Chip, Typography, IconButton, Tooltip } from '@mui/material';
import Grid from '@mui/material/Grid2';

/** Material UI Icons. */
import UploadFileIcon from '@mui/icons-material/UploadFile';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

/** Redux Packages. */
import { useSelector, useDispatch } from 'react-redux';

/** Internal Components. */
import SectionBox from 'shared/ui/GridBox';
import ThemeButton from 'shared/ui/Button';

import Dispute from './Dispute/Dispute';
import DocumentUploadDialog from './DocumentUpload/DocumentUploadDialog';
import useDocumentApplyToDetail from '../hooks/useDocumentApplyToDetail';
import useFetchHistory from '../hooks/useFetchHistory';
import useUserRole from 'shared/hooks/useUserRole';
import { hasOrderManagementQuery, isOrderManagementAvailable } from '../lib/orderManagementAccess';
import UpdateApplySetting from './DetailPanel/UpdateApplySetting';
import UpdateStatement from './DetailPanel/UpdateStatement';
import UpdateStatus from './DetailPanel/Status/UpdateStatus';
import StatusDrawer from './DetailPanel/Status/StatusDrawer';
import UpdateHouse from './DetailPanel/UpdateHouse';
import UpdateTrucking from './DetailPanel/UpdateTrucking';
import OrderManagementPanel from './DetailPanel/OrderManagementPanel';
import {
    oimStatementConfig,
    aimStatementConfig,
    truckingStatementConfig,
    oexStatementConfig,
    aexStatementConfig,
} from 'data/statementConfig';
import { aexTabConfig, aimTabConfig, oexTabConfig, oimTabConfig, truckingTabConfig } from 'data/shipmentDetailConfig';
import {
    oimStatusConfig,
    oexStatusConfig,
    aimStatusConfig,
    aexStatusConfig,
    truckingStatusConfig,
} from 'data/statusConfig';

/** Configuration for each shipment type (indexed by tabValue). */
const SHIPMENT_CONFIGS = {
    0: {
        tabConfig: oimTabConfig,
        statementConfig: oimStatementConfig,
        statusConfig: oimStatusConfig,
        type: 'oim',
        size: [8.1, 2, 1.9],
        height: ['auto', 'auto', 'auto'],
        minHeight: [655, 680, 'auto'],
        formKey: 'oimMainUpdateForm',
    },
    1: {
        tabConfig: oexTabConfig,
        statementConfig: oexStatementConfig,
        statusConfig: oexStatusConfig,
        type: 'oex',
        size: [8, 2, 2],
        height: ['auto', 'auto', 'auto'],
        minHeight: [480, 500, 'auto'],
        formKey: 'oexMainUpdateForm',
    },
    2: {
        tabConfig: aimTabConfig,
        statementConfig: aimStatementConfig,
        statusConfig: aimStatusConfig,
        type: 'aim',
        size: [8.2, 2, 1.8],
        height: ['auto', 'auto', 'auto'],
        minHeight: [480, 508, 'auto'],
        formKey: 'aimMainUpdateForm',
    },
    3: {
        tabConfig: aexTabConfig,
        statementConfig: aexStatementConfig,
        statusConfig: aexStatusConfig,
        type: 'aex',
        size: [8, 2, 2],
        height: ['auto', 'auto', 'auto'],
        minHeight: [745, 770, 'auto'],
        formKey: 'aexMainUpdateForm',
    },
    4: {
        tabConfig: truckingTabConfig,
        statementConfig: truckingStatementConfig,
        statusConfig: truckingStatusConfig,
        type: 'trucking',
        size: [8.1, 1.9, 1.9],
        height: ['auto', 'auto', 'auto'],
        minHeight: [470, 490, 'auto'],
        formKey: 'truckingMainUpdateForm',
    },
};

/** Last measured height per open panel (module-level so it survives virtualizer
 * remounts). Lets a remounting panel first paint a same-height shell, deferring
 * the heavy content tree until the browser idles, so scrolling past an open
 * panel neither shifts the scroll length nor drops frames. */
const lastPanelHeights = new Map();

/** Which view (operation / orderManagement) each shipment's panel last showed. The panel unmounts while
 * scrolled out of the grid viewport, so local state alone would snap back to Operation on remount. */
const lastDetailViews = new Map();

/** Grid-scroll quiet period that must pass before a remounted panel hydrates. */
const HYDRATE_AFTER_SCROLL_QUIET_MS = 200;

const ShipmentDetail = ({ rowData, apiRef }) => {
    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** Value for shipment type(OIM/OEX/AIM/AEX/Trucking = 0~4). */
    const tabValue = useSelector((state) => state.tabValueForm);
    /** Roles of the signed-in user (Auth0). */
    const userRole = useUserRole();
    /** shipment progress status (inprogress/complete/dispute). */
    const shipmentProgress = useSelector((state) => state.progressValue);

    /** Fetch and register il_history for this row; isReady mirrors initial data load. */
    const { isReady } = useFetchHistory(tabValue, rowData.id);

    /** Height shell + deferred hydration across virtualizer remounts. */
    const heightKey = `${tabValue}-${rowData.id}`;
    const cachedHeight = lastPanelHeights.get(heightKey);
    const [hydrated, setHydrated] = useState(() => cachedHeight === undefined);
    const rootRef = useRef(null);

    /** Hydrate only after the grid scroll has been quiet for a beat. An idle-callback
     * deadline could fire mid-flick and mount the heavy tree (nudging the measured
     * height) while the list is still moving — felt as a bounce on fast scrolls. */
    useEffect(() => {
        if (hydrated) return undefined;
        let timerId = setTimeout(() => setHydrated(true), HYDRATE_AFTER_SCROLL_QUIET_MS);
        const unsubscribe = apiRef.current.subscribeEvent('scrollPositionChange', () => {
            clearTimeout(timerId);
            timerId = setTimeout(() => setHydrated(true), HYDRATE_AFTER_SCROLL_QUIET_MS);
        });
        return () => {
            clearTimeout(timerId);
            unsubscribe();
        };
    }, [hydrated]);

    /** Record the final panel height before the virtualizer detaches the node
     * (layout effect cleanup still sees the mounted DOM; a passive effect would
     * read 0 from a detached node). */
    useLayoutEffect(() => {
        const node = rootRef.current;
        return () => {
            if (node) {
                lastPanelHeights.set(heightKey, node.offsetHeight);
            }
        };
    }, [heightKey]);

    /** Dynamic width for detail panel, sized synchronously so the first frame after a
     * virtualizer remount already lays out at the real width (a 0-width first paint
     * would distort the panel height the grid measures for its scroll length). */
    const [width, setWidth] = useState(() => {
        const dimensions = apiRef.current?.getRootDimensions();
        return dimensions ? dimensions.viewportOuterSize.width - 20 : 0;
    });
    /** State that stores the value of the house currently being viewed. */
    const [selectedHouseRow, setSelectedHouseRow] = useState({});
    /** Current HBL tab index inside UpdateHBL component. */
    const [hblTabValue, setHblTabValue] = useState(0);
    /** Whether the enlarged Status drawer is open. */
    const [statusDrawerOpen, setStatusDrawerOpen] = useState(false);
    /** State for document upload dialog (Detail panel: add HBL/HAWB from parsed PDFs). */
    const [documentUploadOpen, setDocumentUploadOpen] = useState(false);
    /** Whether this page load's URL carries ?ordermanagement=1 (read once; the URL does not change while the panel lives). */
    const orderManagementQuery = useMemo(() => hasOrderManagementQuery(), []);
    /** Order Management is not released yet: it needs the URL flag AND an Administrator, on an Ocean/Air Import shipment. */
    const orderManagementEnabled = isOrderManagementAvailable(userRole, tabValue, orderManagementQuery);
    /** Which view the user picked: the operation panel or the optional Order Management (packing list) panel.
     * Seeded from the per-shipment cache so a virtualizer remount keeps the user's choice. */
    const [detailView, setDetailViewState] = useState(() => lastDetailViews.get(heightKey) ?? 'operation');
    /** Without access the panel always shows Operation, even if a cached choice says otherwise. */
    const visibleView = orderManagementEnabled ? detailView : 'operation';
    const setDetailView = useCallback(
        (view) => {
            lastDetailViews.set(heightKey, view);
            setDetailViewState(view);
        },
        [heightKey]
    );
    const closeDocumentUpload = useCallback(() => setDocumentUploadOpen(false), []);

    /** Extract configuration based on selected shipment type. */
    const currentConfig = useMemo(() => {
        const config = SHIPMENT_CONFIGS[tabValue];
        if (!config) {
            return null;
        }
        return config;
    }, [tabValue]);

    const formKey = currentConfig ? currentConfig.formKey : null;

    const latestMainRow = useSelector((state) => {
        if (!formKey) return null;
        return state[formKey]?.find((item) => item.id === rowData.id);
    });

    const mergedRow = useMemo(() => {
        if (!latestMainRow) return rowData;
        return { ...rowData, ...latestMainRow };
    }, [rowData, latestMainRow]);

    /** Apply parsed document data to existing shipment (HBL/HAWB + optional MBL update). */
    const handleApplyParsedData = useDocumentApplyToDetail(
        rowData,
        currentConfig?.tabConfig ?? null,
        tabValue,
        closeDocumentUpload
    );

    /** Handles changes in viewport size and updates width accordingly.
     * Same -20 offset as the initial state so a viewport event never nudges the
     * width (and therefore the measured panel height) by itself. */
    const handleViewportInnerSizeChange = useCallback(() => {
        const dimensions = apiRef.current.getRootDimensions();
        setWidth(dimensions.viewportOuterSize.width - 20);
    }, []);

    /** Setup listener for DataGrid viewport size on mount.
     * and clean up on unmount.
     */
    useEffect(() => {
        // Subscribe to viewport size changes
        const unsubscribe = apiRef.current.subscribeEvent('viewportInnerSizeChange', handleViewportInnerSizeChange);

        // Cleanup subscription on unmount
        return () => unsubscribe();
    }, []);

    /** Early return if no configuration found */
    if (!currentConfig) return null;

    /** Extract configuration based on selected shipment type. */
    const { tabConfig, statementConfig, statusConfig, type, size, height, minHeight } = currentConfig;

    return (
        <Stack
            ref={rootRef}
            sx={{
                p: 1,
                /* Before hydration, hold the exact height measured on unmount so the
                 * grid's scroll length does not move while the heavy tree is deferred. */
                height: hydrated ? '100%' : cachedHeight,
                boxSizing: 'border-box',
                position: 'sticky',
                left: 0,
                bgcolor: '#f8f8f8',
                width,
            }}
            direction="column"
        >
            {hydrated && !isReady && (
                /* Hold the panel at its expected height while history loads: an empty
                 * panel would be measured as ~0 by the grid's auto-height tracking,
                 * shrinking the scroll length and yanking the scroll position down. */
                <Box sx={{ height: minHeight[1] }} />
            )}
            {hydrated && isReady && orderManagementEnabled && (
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    {[
                        ['operation', 'Operation'],
                        ['orderManagement', 'Order Management'],
                    ].map(([key, label]) => (
                        <Chip
                            key={key}
                            label={label}
                            size="small"
                            clickable
                            color="primary"
                            variant={visibleView === key ? 'filled' : 'outlined'}
                            onClick={() => setDetailView(key)}
                            sx={{ fontSize: 12, fontWeight: 500 }}
                        />
                    ))}
                </Box>
            )}
            {hydrated && isReady && visibleView === 'orderManagement' && (
                <OrderManagementPanel mainRow={mergedRow} tabConfig={tabConfig} />
            )}
            {hydrated && isReady && visibleView === 'operation' && (
                <Grid container spacing={1}>
                    {shipmentProgress !== 'inprogress' && <Dispute row={rowData} />}
                    <Box sx={{ width: '100%' }}>
                        <UpdateApplySetting row={rowData} tabConfig={tabConfig} />
                    </Box>
                    <Grid size={size[0]} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 1,
                            }}
                        >
                            <Typography sx={{ color: '#01354d', fontSize: '13px', fontWeight: 500 }}>
                                Shipment Detail
                            </Typography>
                            {type !== 'trucking' && (
                                <ThemeButton
                                    text="Upload"
                                    icon={<UploadFileIcon />}
                                    onClick={() => setDocumentUploadOpen(true)}
                                    sx={{ height: '27px', fontSize: '11px', minWidth: '70px' }}
                                />
                            )}
                        </Box>
                        <Box
                            sx={{
                                borderRadius: '10px',
                                boxShadow: '0px 1px 2px 1px rgba(0, 0, 0, 0.1)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignContent: 'center',
                                p: 0,
                                gap: '5px',
                                bgcolor: 'white',
                                height: height[0],
                            }}
                        >
                            {type === 'trucking' ? (
                                <UpdateTrucking
                                    row={mergedRow}
                                    presetData={mergedRow.il_opus_reference_preset}
                                    tabConfig={tabConfig}
                                />
                            ) : (
                                <>
                                    <DocumentUploadDialog
                                        open={documentUploadOpen}
                                        onClose={() => setDocumentUploadOpen(false)}
                                        tabValue={tabValue}
                                        dispatch={dispatch}
                                        onOpenManualEntry={() => setDocumentUploadOpen(false)}
                                        embedInDetailPanel
                                        onApplyToForm={handleApplyParsedData}
                                    />
                                    <UpdateHouse
                                        mainRow={mergedRow}
                                        presetData={mergedRow.il_opus_reference_preset}
                                        tabConfig={tabConfig}
                                        selectedHouseRow={selectedHouseRow}
                                        setSelectedHouseRow={setSelectedHouseRow}
                                        hblTabValue={hblTabValue}
                                        setHblTabValue={setHblTabValue}
                                    />
                                </>
                            )}
                        </Box>
                    </Grid>
                    {Array.isArray(statementConfig) ? (
                        statementConfig.map((config, index) => (
                            <SectionBox key={index} size={size[index + 1]} sx={{ p: 1, height: height[index + 1] }}>
                                <UpdateStatement
                                    key={index}
                                    row={rowData}
                                    presetData={mergedRow.il_opus_reference_preset}
                                    statementConfig={config}
                                />
                            </SectionBox>
                        ))
                    ) : (
                        <SectionBox size={size[1]} sx={{ p: 1, height: height[1] }}>
                            <UpdateStatement
                                row={rowData}
                                presetData={mergedRow.il_opus_reference_preset}
                                statementConfig={statementConfig}
                            />
                        </SectionBox>
                    )}
                    <SectionBox
                        title={
                            <Box
                                component="span"
                                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                            >
                                Status
                                <Tooltip title="Open in drawer">
                                    <IconButton
                                        size="small"
                                        aria-label="open status drawer"
                                        onClick={() => setStatusDrawerOpen(true)}
                                        sx={{ p: 0, mr: '8px', color: '#01354d' }}
                                    >
                                        <OpenInFullIcon sx={{ fontSize: 17 }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        }
                        size={size[size.length - 1]}
                        sx={{ p: 0, height: height[height.length - 1] }}
                    >
                        <UpdateStatus
                            row={rowData}
                            statusConfig={statusConfig}
                            selectedHouseRow={selectedHouseRow}
                            hblTabValue={hblTabValue}
                        />
                    </SectionBox>
                    <StatusDrawer
                        open={statusDrawerOpen}
                        onClose={() => setStatusDrawerOpen(false)}
                        row={rowData}
                        statusConfig={statusConfig}
                        selectedHouseRow={selectedHouseRow}
                        hblTabValue={hblTabValue}
                    />
                </Grid>
            )}
        </Stack>
    );
};

export default React.memo(ShipmentDetail);
